/**
 * Appels OpenAI via le backend uniquement — la clé n'est jamais exposée au frontend.
 * En dev, local et prod : seule OPENAI_API_KEY côté serveur est utilisée.
 * Ne pas réintroduire de variable Vite (VITE_OPENAI_*) pour éviter les fuites.
 */

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatCompletionOptions {
  model?: string;
  messages: ChatMessage[];
  temperature?: number;
  max_tokens?: number;
}

/**
 * Vérifie si OpenAI est configuré côté serveur (sans exposer la clé).
 */
export async function isOpenAIConfigured(): Promise<boolean> {
  try {
    const res = await fetch('/api/openai/status', { credentials: 'include' });
    const data = await res.json();
    return !!data?.configured;
  } catch {
    return false;
  }
}

/**
 * Appel chat completions via le backend (proxy). La clé reste côté serveur.
 */
export async function chatCompletions(options: ChatCompletionOptions): Promise<string> {
  const { model = 'gpt-4o', messages, temperature = 0.7, max_tokens = 4000 } = options;
  const res = await fetch('/api/openai/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ model, messages, temperature, max_tokens }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error?.message || data?.error || res.statusText;
    throw new Error(msg || `Erreur OpenAI: ${res.status}`);
  }
  return data.choices?.[0]?.message?.content ?? '';
}
